// The call flow host (CU-869fmc59c), mounted once at app level next to CallPillHost.
//
// One place runs the three things staff do with a call, whichever screen they start from
// (Call Logs table, To call back queue, call pill, recording modal, UX-A's menu). It builds no
// screens of its own: it routes to the ones the CRM already has.
//   openOutcome(call)    a lead (any status) -> the existing contact popup (follow-up step);
//                        an unknown caller -> the Create Lead popup (no question)
//   logNoAnswer(call)    an unknown caller's row: one call-back attempt ("Try n of 3 logged")
//   openCreateLead(call) the main Create Lead popup, pre-filled from the call (its own search box
//                        runs on the number first)
//   openLinkLead(call)   suggestions + search, link this call and the number's earlier calls
// `call` is a Call Logs row, a queue row or a live pill call (callFlowModel.normaliseCall).
//   openCallBack(call)   Call back from a row, queue row or missed-call pill (startCallBack is the same)
//   startCallBack(row)   the Call back button: claim (queue rows), open the dialer only, and ask
//                        about the call (in the widget) when it ends
//   answerAsk(call, id)  the widget's answer to "Did they answer?"
// From code that is not React: requestCallFlow('outcome' | 'createLead' | 'linkLead' | 'callBack', call, options),
// i.e. window.dispatchEvent(new CustomEvent('call-flow:open', { detail: { action, call, options } })).
// Every save announces `call-flow:changed`, which the Call Logs page answers by reloading
// the queue and the table.
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { dispatch, store, useSelector } from '../../redux/store';
import { useAuth } from '../../hooks';
import { useOutbound } from '../outbound/OutboundCallProvider';
import { patientPath } from '../callPill/patientPath';
import { claimNumber } from '../../pages/dashboard/callLogs/queue/queueApi';
import { claimChip, readClaim } from '../../pages/dashboard/callLogs/queue/queueModel';
import { CALL_BACK_DIALER, callBackDialer, callBackNumber, requestDialer } from '../../pages/dashboard/callLogs/rowActions';
import { formatUkPhone } from '../../utils/ukPhone';
import { openCreateLeadDialog } from '../../redux/slices/createLeadDialog';
import { clearLead, saveNewLeadDetail } from '../../redux/slices/lead';
import { setCurrentTreatmentDataGlobal } from '../../redux/slices/multiTreatment';
import { localStorageRemoveItem } from '../../utils/storage-available';
import newIdempotencyKey from '../../utils/idempotencyKey';
import { recordCallOutcome, undoCallAttempt } from '../../pages/dashboard/callLogs/callOutcome.api';
import { fetchLeadPrefill, linkCall, markNotAPatient, undoNotAPatient } from '../../pages/dashboard/callLogs/caller/callerActions.api';
import { COULDNT_CONFIRM, WRITE, logCallOutcome, readWrite, undoCallOutcome } from '../callPill/callOutcome';
import { findOwedMissedCall, sameNumber } from '../callPill/callPillOutcome';
import {
    ASK_CHOICE, AWAIT_END_TTL_MS, CALL_FLOW_ACTION, CALL_FLOW_ASK_EVENT, CALL_FLOW_AWAIT_END_EVENT, CALL_FLOW_EVENT, CALL_FLOW_LEAD_CREATED_EVENT,
    CREATE_LEAD_FORM_STATE_KEY, TWILIO_CALL_ENDED_EVENT, requestCallFlow, announceCallFlowChange, buildCallLeadPrefill,
    canFlagNotAPatient, dialedCall, includeEarlierCalls, normaliseCall, outcomeForCard, ownOutcomeForCard, tryNumber, withLead, CARD,
} from './callFlowModel';
import FlowBoundary from './FlowBoundary';
import CallOutcomeScreen from './CallOutcomeScreen';
import LinkLeadDialog from './LinkLeadDialog';
import { callLayer } from '../callPill/callLayers';

// A stand-in call won't replace a real one the widget asked about in the last two minutes.
const ASK_STALE_MS = 2 * 60 * 1000;
const KIND = { OUTCOME: 'outcome', LINK: 'link' };

const clearCreateLeadFormState = () => {
    try { window.sessionStorage.removeItem(CREATE_LEAD_FORM_STATE_KEY); } catch { /* no storage: nothing to clear */ }
};

const CallFlowContext = createContext(null);

export { CALL_FLOW_EVENT, requestCallFlow };

// Every public call is a no-op (with a warning while developing) for a call with no usable id.
const usableCall = (input, name) => {
    const call = normaliseCall(input);
    if (!call) {
        let dev = false;
        try { dev = Boolean(import.meta.env?.DEV); } catch { dev = false; }
        // eslint-disable-next-line no-console
        if (dev) console.warn(`useCallFlow().${name}: the call has no usable id, nothing to do.`);
    }
    return call;
};

// Outside the provider (tests, public pages) the calls go out as window events, so nothing throws
// and a provider that is mounted later still hears them.
const FALLBACK = {
    openOutcome: (call, options = {}) => requestCallFlow(CALL_FLOW_ACTION.OUTCOME, call, options),
    answerAsk: () => Promise.resolve({ ok: false }),
    askAboutCall: () => {},
    logNoAnswer: () => Promise.resolve({ ok: false }),
    openCreateLead: (call) => requestCallFlow(CALL_FLOW_ACTION.CREATE_LEAD, call),
    openLinkLead: (call) => requestCallFlow(CALL_FLOW_ACTION.LINK_LEAD, call),
    startCallBack: (call) => requestCallFlow(CALL_FLOW_ACTION.CALL_BACK, call),
    openCallBack: (call) => requestCallFlow(CALL_FLOW_ACTION.CALL_BACK, call),
    awaitCallEnd: (call) => { if (typeof window !== 'undefined' && call) window.dispatchEvent(new CustomEvent(CALL_FLOW_AWAIT_END_EVENT, { detail: { call } })); },
    noteOwnCallNeedsOutcome: () => {},
    noteOwnCallAutoLogged: () => {},
};

export const useCallFlow = () => useContext(CallFlowContext) || FALLBACK;

export default function CallFlowProvider({ children }) {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const callLogsLevel = useSelector((state) => (String(state.permissions?.practiceId) === String(state.practiceTreatmentList?.practiceId)
        ? state.permissions?.permissions?.['call-logs'] : null));
    const canManageCallLogs = Boolean(user?.is_superuser) || callLogsLevel === 'manage';
    const outbound = useOutbound();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // The one open screen: { seq, kind, call, opts, practiceId } or null.
    const [flow, setFlow] = useState(null);
    const seq = useRef(0);
    const practiceRef = useRef(practiceId);
    practiceRef.current = practiceId;
    // One key per user intent: the screen that is open (`seq`), the call and the outcome. Pressing the
    // same answer again on the same screen (a retry) reuses it, so the server drops the repeat; a new
    // screen for the same call is a new attempt and gets a new key.
    const keys = useRef({});
    const keyFor = (...parts) => {
        const sig = parts.join('|');
        keys.current[sig] = keys.current[sig] || newIdempotencyKey();
        return keys.current[sig];
    };
    // Create lead opened by the flow: what to do when the wizard reports the saved lead.
    const wizardRef = useRef(null);
    // The screen (seq) whose outcome has been written; closing a "must clear" screen without one posts `spoke`.
    const doneSeq = useRef(0);
    // A call-back that was started for a row; the outcome screen opens when that call ends.
    const pending = useRef(null);

    useEffect(() => { setFlow(null); pending.current = null; wizardRef.current = null; }, [practiceId]);

    const show = useCallback((kind, call, opts = {}) => {
        if (!practiceRef.current || !call) return;
        // The contact popup shares lead state with an open Create Lead form: never open it over one.
        if (kind === KIND.OUTCOME && store.getState().createLeadDialog?.open) {
            enqueueSnackbar('Finish or close the lead you are creating first.', { variant: 'warning' });
            return;
        }
        seq.current += 1;
        setFlow({ seq: seq.current, kind, call, opts, practiceId: practiceRef.current });
    }, [enqueueSnackbar]);
    const close = useCallback(() => { setFlow(null); }, []);
    const changed = useCallback(() => announceCallFlowChange(), []);

    // ── Create lead ──────────────────────────────────────────────────────────
    const startWizard = useCallback(async (call, opts = {}) => {
        const forPractice = practiceRef.current;
        if (!forPractice) return;
        close();
        if (store.getState().createLeadDialog?.open) {
            enqueueSnackbar('Finish or close the lead you are creating first.', { variant: 'warning' });
            return;
        }
        // The server's pre-fill; the call fills every gap (and an older backend has none). A dialed
        // call has no row the server knows.
        const serverPrefill = call.synthetic ? null : await fetchLeadPrefill(forPractice, call.row).catch(() => null);
        if (String(practiceRef.current) !== String(forPractice)) return;
        if (store.getState().createLeadDialog?.open) return;
        const { personalDetail } = buildCallLeadPrefill(call, serverPrefill);
        // The prefill wins over the form state an earlier, abandoned lead left in the session.
        clearCreateLeadFormState();
        localStorageRemoveItem('localTreatmentData');
        wizardRef.current = { callId: call.id, opts };
        // A clean slate: nothing of an earlier lead (the follow-up step stores its lead as the
        // "created" one, and booking details linger) may carry into this one.
        dispatch(clearLead());
        dispatch(saveNewLeadDetail({ personalDetail }));
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(openCreateLeadDialog({ call, baselineLeadId: store.getState().lead?.createLead?.id ?? null }));
    }, [close, enqueueSnackbar]);

    // Straight to the popup: its own "Search existing leads" box runs on the number first (F2).
    const openCreateLead = useCallback((input, opts = {}) => {
        const call = normaliseCall(input);
        if (call) startWizard(call, opts);
    }, [startWizard]);

    // ── Writing the outcome ──────────────────────────────────────────────────
    const warn = useCallback((text) => enqueueSnackbar(text, { variant: 'warning' }), [enqueueSnackbar]);

    // `outcomeKey`: booked | spoke | no_answer | not_a_patient. Only a call that is owed one is written:
    // a missed inbound call with an open call-back (Call Logs outcome endpoint), or this user's own
    // outbound call (the pill's call-outcome endpoint). Anything else has nothing to record.
    const writeOutcome = useCallback(async (current, outcomeKey, { newLead = false, quiet = false, force = false } = {}) => {
        const { call, practiceId: forPractice } = current;
        if (call.ownOutbound) {
            const ownKey = outcomeKey === 'not_a_patient' ? 'not_a_patient' : ownOutcomeForCard(
                { booked: 'Booking Details', spoke: 'Appointment Not Booked' }[outcomeKey], { newLead },
            );
            if (!ownKey) return { ok: true, skipped: true };
            const result = readWrite(await logCallOutcome(forPractice, call.pillCallId, ownKey, keyFor(current.seq, call.id, ownKey)), 'The outcome could not be saved.');
            if (result.kind === WRITE.OK) {
                enqueueSnackbar(result.outcome?.label || 'Outcome saved.', {
                    variant: 'success',
                    autoHideDuration: 10000,
                    action: ownKey === 'not_a_patient' && result.outcome?.undoAllowed ? (key) => (
                        <Button size="small" color="inherit" data-testid="outcome-undo" onClick={async () => {
                            closeSnackbar(key);
                            const undone = readWrite(await undoCallOutcome(forPractice, call.pillCallId), 'Could not undo.');
                            if (undone.kind === WRITE.OK) enqueueSnackbar('Undone: that number counts again.', { variant: 'success' });
                            else warn(undone.kind === WRITE.UNKNOWN ? COULDNT_CONFIRM : undone.message);
                            changed();
                        }}>Undo</Button>
                    ) : undefined,
                });
                return { ok: true };
            }
            if (result.kind === WRITE.UNKNOWN) { warn(COULDNT_CONFIRM); return { ok: false, uncertain: true }; }
            warn(result.message);
            return { ok: false, message: result.message };
        }
        // `force`: an explicit row action lets the server decide (it refuses what isn't a missed inbound call).
        if (!call.owed && !force) return { ok: true, skipped: true };
        if (outcomeKey === 'not_a_patient') {
            const result = await markNotAPatient(forPractice, call.row, '', keyFor(current.seq, call.id, 'not_a_patient'));
            if (result.ok) {
                const flagId = result.data?.flag?.id;
                enqueueSnackbar('Marked as not a patient. Calls from this number are no longer owed a call-back.', {
                    variant: 'success',
                    autoHideDuration: 10000,
                    action: flagId ? (key) => (
                        <Button size="small" color="inherit" data-testid="outcome-undo" onClick={async () => {
                            closeSnackbar(key);
                            const undone = await undoNotAPatient(forPractice, flagId);
                            if (undone.ok) enqueueSnackbar('Undone. Calls from this number count again.', { variant: 'success' });
                            else warn(undone.uncertain ? COULDNT_CONFIRM : undone.message);
                            changed();
                        }}>Undo</Button>
                    ) : undefined,
                });
                return { ok: true };
            }
            if (result.uncertain) { warn(COULDNT_CONFIRM); return { ok: false, uncertain: true }; }
            warn(result.message);
            return { ok: false, message: result.message };
        }
        const result = await recordCallOutcome(forPractice, call.row, outcomeKey, { idempotencyKey: keyFor(current.seq, call.id, outcomeKey) });
        if (result.ok) {
            if (!quiet) enqueueSnackbar('Call outcome saved.', { variant: 'success' });
            return { ok: true, data: result.data };
        }
        if (result.uncertain) { warn(COULDNT_CONFIRM); return { ok: false, uncertain: true }; }
        // quiet + 400: the caller says it in its own words (a plain info toast).
        if (!(quiet && result.status === 400)) warn(result.message);
        return { ok: false, status: result.status, message: result.message };
    }, [changed, closeSnackbar, enqueueSnackbar, warn]);

    // The follow-up step saved a card for the lead.
    const onLeadStepComplete = useCallback(async (current, { outcome }) => {
        doneSeq.current = current.seq;
        const key = outcomeForCard(outcome);
        if (key) await writeOutcome(current, key, { newLead: Boolean(current.opts.createdInFlow) });
        changed();
    }, [changed, writeOutcome]);

    // The short choice for a patient past enquiry (or archived): Log outcome is an explicit ask, so the
    // server decides whether the call is owed one (`force`). Closes once saved; a save that may have
    // gone through keeps the screen, locked, and the lists refresh.
    const chooseStageOutcome = useCallback(async (current, key) => {
        const result = await writeOutcome(current, key, { force: true });
        if (result.ok) {
            doneSeq.current = current.seq;
            close();
            changed();
        } else if (result.uncertain) {
            doneSeq.current = current.seq;
            changed();
        }
        return result;
    }, [changed, close, writeOutcome]);

    // Closing a screen. A call that was just linked or created for must still clear To call back:
    // closed without choosing a card, it is recorded as `spoke` (every outcome but No answer clears).
    const closeScreen = useCallback((current) => {
        close();
        if (current?.opts?.mustClear && doneSeq.current !== current.seq) {
            doneSeq.current = current.seq;
            writeOutcome(current, 'spoke', { newLead: Boolean(current.opts.createdInFlow) }).finally(changed);
        }
    }, [changed, close, writeOutcome]);

    // ── After a link / a created lead ────────────────────────────────────────
    const afterLinked = useCallback((current, lead, { createdInFlow = false } = {}) => {
        changed();
        const linked = withLead(current.call, lead);
        // The call was owed an outcome: offer the follow-up step for the lead it now belongs to.
        if (current.call.owed || current.call.ownOutbound) { show(KIND.OUTCOME, linked, { createdInFlow, mustClear: true }); return; }
        close();
        enqueueSnackbar(`Call linked to ${linked.lead.name}.`, { variant: 'success' });
    }, [changed, close, enqueueSnackbar, show]);

    // The wizard saved a lead for a call: link the call (and earlier ones), refresh, carry on.
    useEffect(() => {
        const onCreated = async (e) => {
            const { leadId, call: raw } = e.detail || {};
            const call = normaliseCall(raw);
            const forPractice = practiceRef.current;
            if (!call || leadId == null || !forPractice) return;
            const wizard = wizardRef.current?.callId === call.id ? wizardRef.current : null;
            wizardRef.current = null;
            let linkedOk = true;
            if (!call.isWhatsapp && !call.synthetic) {
                const result = await linkCall(forPractice, call.row, { leadId, includeEarlier: includeEarlierCalls(call) }, keyFor(call.id, 'link', leadId));
                // 404: an older backend without the link endpoint: the lead stands and matching picks it up.
                if (!result.ok && result.status !== 404) {
                    linkedOk = false;
                    warn(result.uncertain
                        ? "The lead was created. We couldn't confirm the call was linked; refresh the list to check."
                        : `The lead was created, but the call wasn't linked (${result.message}). Use Link to existing on the row.`);
                }
            }
            if (String(practiceRef.current) !== String(forPractice)) return;
            changed();
            // An owed call (or one the unknown-caller screen sent here) goes on to the outcome step; an
            // owed one must clear To call back even if that step is closed without a card.
            if (linkedOk && (call.owed || call.ownOutbound || wizard?.opts?.thenOutcome)) {
                show(KIND.OUTCOME, withLead(call, { id: leadId }), { createdInFlow: true, mustClear: Boolean(call.owed || call.ownOutbound) });
            } else if (linkedOk) {
                enqueueSnackbar('Lead created and call linked.', { variant: 'success' });
            }
        };
        window.addEventListener(CALL_FLOW_LEAD_CREATED_EVENT, onCreated);
        return () => window.removeEventListener(CALL_FLOW_LEAD_CREATED_EVENT, onCreated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changed, enqueueSnackbar, show, warn]);

    // ── "Did they answer?" in the dialer widget ──────────────────────────────
    // For a call whose result we can't read (placed through the Twilio browser widget, or the handset
    // state's "I've called them"). The widget hosts the question in its finished state and cancels the
    // event to say it took it. A user with no dialer widget gets the next best thing: the existing
    // contact popup for a lead, the Create Lead popup for a caller we don't know.
    const askSeq = useRef(0);
    const undoUnsupported = useRef(false);
    const askRef = useRef(null);
    const askInWidget = useCallback((call) => {
        // A stand-in call (a number and nothing else) never replaces the real call the widget is already
        // asking about, e.g. a second "call ended" for the same hang-up.
        const open = askRef.current;
        if (call.synthetic && open && !open.call.synthetic && Date.now() - open.at < ASK_STALE_MS && sameNumber(open.call.number, call.number)) return;
        askSeq.current += 1;
        askRef.current = { call, seq: `ask${askSeq.current}`, practiceId: practiceRef.current, at: Date.now() };
        const detail = { call, canNotAPatient: canFlagNotAPatient(call, { canManageCallLogs }) };
        const taken = typeof window !== 'undefined' && !window.dispatchEvent(new CustomEvent(CALL_FLOW_ASK_EVENT, { cancelable: true, detail }));
        if (taken) return;
        if (call.lead?.id) show(KIND.OUTCOME, call, { afterCall: false });
        else startWizard(call, { thenOutcome: true });
    }, [canManageCallLogs, show, startWizard]);

    // The widget's "I've called them" (the handset state, opened for a call-back row): ask about that row in
    // the same finished state. The waiting call-back is used when it is still there; else the number's owed
    // missed call; else the number alone (nothing is written or linked). Never a bare id.
    // The widget was closed without an answer: forget the question, so a quick redial gets its own.
    const forgetAsk = useCallback(() => { askRef.current = null; }, []);

    const askAboutCall = useCallback(async ({ callId = null, number = '', leadId = null, leadName = '' } = {}) => {
        const forPractice = practiceRef.current;
        const waiting = pending.current && String(pending.current.call.row?.id) === String(callId) ? pending.current.call : null;
        if (waiting) pending.current = null;
        let call = waiting;
        if (!call && number) {
            const owed = await findOwedMissedCall(forPractice, number).catch(() => ({ row: null }));
            call = owed?.row ? normaliseCall(owed.row) : null;
        }
        if (!call && (number || leadId != null)) call = dialedCall({ number, leadId, leadName });
        if (call && String(practiceRef.current) === String(forPractice)) askInWidget(call);
    }, [askInWidget]);

    // One write at a time per call and answer (a double press writes once). After "couldn't confirm" the
    // retry reuses the same key; any other result ends the intent, so the next press is a new one.
    const inFlight = useRef(new Set());
    const intents = useRef({});
    const guarded = useCallback(async (call, outcomeKey, run) => {
        const sig = `${call.id}|${outcomeKey}`;
        if (inFlight.current.has(sig)) return { ok: false, busy: true };
        inFlight.current.add(sig);
        if (!intents.current[sig]) { askSeq.current += 1; intents.current[sig] = `w${askSeq.current}`; }
        try {
            const result = await run(intents.current[sig]);
            if (!result.uncertain) delete intents.current[sig];
            return result;
        } finally {
            inFlight.current.delete(sig);
        }
    }, []);

    // The widget's answer. Spoke carries on in the existing screens; No answer / Not a patient are written
    // here. A lead that isn't owed a call-back gets the contact popup's own No answer step instead, so the
    // follow-up count moves.
    const answerAsk = useCallback(async (input, choice) => {
        const call = normaliseCall(input);
        if (!call) return { ok: false };
        if (choice === ASK_CHOICE.SPOKE) {
            // An owed call still clears To call back if the popup is closed without a saved card.
            if (call.lead?.id) show(KIND.OUTCOME, call, { afterCall: true, mustClear: Boolean(call.owed || call.ownOutbound) });
            else startWizard(call, { thenOutcome: true });
            return { ok: true };
        }
        if (choice === ASK_CHOICE.NO_ANSWER && call.lead?.id && !call.owed && !call.ownOutbound) {
            show(KIND.OUTCOME, call, { afterCall: false, initialCard: CARD.NO_ANSWER });
            return { ok: true };
        }
        const key = choice === ASK_CHOICE.NOT_A_PATIENT ? 'not_a_patient' : 'no_answer';
        const result = await guarded(call, key, (intent) => {
            const asked = askRef.current?.call?.id === call.id ? askRef.current : { call, practiceId: practiceRef.current };
            return writeOutcome({ ...asked, seq: intent }, key);
        });
        if (result.busy) return result;
        if (result.ok && result.skipped && key === 'no_answer') enqueueSnackbar('Noted. Nothing was waiting for a call-back from this number.', { variant: 'info' });
        if (result.ok) askRef.current = null;
        if (result.ok || result.uncertain) changed();
        return result;
    }, [changed, enqueueSnackbar, guarded, show, startWizard, writeOutcome]);

    // "No answer – try again later" on an unknown caller's row: one call-back attempt, no question. The
    // server decides whether the call is owed one: its refusal shows as a plain info toast, never "logged".
    // The toast's Undo calls `outcome/undo/` with this press's idempotency key.
    const logNoAnswer = useCallback(async (input) => {
        const call = usableCall(input, 'logNoAnswer');
        if (!call) return { ok: false };
        const forPractice = practiceRef.current;
        let usedKey = '';
        const result = await guarded(call, 'no_answer', (intent) => {
            usedKey = intent;
            return writeOutcome({ call, seq: intent, practiceId: forPractice }, 'no_answer', { quiet: true, force: true });
        });
        if (result.busy) return result;
        if (result.ok && !result.skipped) {
            const n = tryNumber(result.data);
            const key = keyFor(usedKey, call.id, 'no_answer');
            enqueueSnackbar(n ? `Try ${n} of 3 logged.` : 'No answer logged.', {
                variant: 'success',
                autoHideDuration: 10000,
                action: undoUnsupported.current || call.synthetic ? undefined : (snackKey) => (
                    <Button size="small" color="inherit" data-testid="outcome-undo" onClick={async () => {
                        closeSnackbar(snackKey);
                        const undone = await undoCallAttempt(forPractice, call.row, { idempotencyKey: key });
                        if (undone.ok) enqueueSnackbar('Undone: that try was taken back.', { variant: 'success' });
                        else if (undone.unsupported) { undoUnsupported.current = true; warn("Undo isn't available yet."); }
                        else if (undone.uncertain) warn("Couldn't confirm the undo. Refresh to check.");
                        else warn(undone.message);
                        changed();
                    }}>Undo</Button>
                ),
            });
        } else if (!result.ok && result.status === 400) {
            enqueueSnackbar(result.message || 'No answer can only be logged for a missed inbound call.', { variant: 'info' });
        }
        if (result.ok || result.uncertain) changed();
        return result;
    }, [changed, closeSnackbar, enqueueSnackbar, guarded, warn, writeOutcome]);

    // ── Public API ───────────────────────────────────────────────────────────
    // A lead (any status) goes to the existing contact popup. A caller we don't know goes to the
    // Create Lead popup: no question here (that is asked only in the dialer widget after a call).
    const openOutcome = useCallback((input, { afterCall = false } = {}) => {
        const call = usableCall(input, 'openOutcome');
        if (!call) return;
        if (call.lead?.id) show(KIND.OUTCOME, call, { afterCall });
        else startWizard(call, { thenOutcome: true });
    }, [show, startWizard]);
    const openCreateLeadPublic = useCallback((input) => {
        const call = usableCall(input, 'openCreateLead');
        if (call) openCreateLead(call);
    }, [openCreateLead]);
    const openLinkLead = useCallback((input) => {
        const call = usableCall(input, 'openLinkLead');
        if (call) show(KIND.LINK, call, {});
    }, [show]);

    // Call back: the dialer opens alone; the outcome screen follows when the call has ended.
    const awaitCallEnd = useCallback((input) => {
        const call = normaliseCall(input);
        if (!call) return;
        const forPractice = practiceRef.current;
        pending.current = { call, at: Date.now(), practiceId: forPractice };
        // Call back from the pill: the outcome belongs to the missed call that is owed, not to the pill's call.
        if (call.source === 'pill' && !call.owed && call.number) {
            findOwedMissedCall(forPractice, call.number).then(({ row }) => {
                const owed = row ? normaliseCall(row) : null;
                if (owed && pending.current?.call === call && String(practiceRef.current) === String(forPractice)) {
                    pending.current = { ...pending.current, call: owed };
                }
            });
        }
    }, []);
    const takePending = useCallback((number = null) => {
        const p = pending.current;
        if (!p || String(p.practiceId) !== String(practiceRef.current) || Date.now() - p.at > AWAIT_END_TTL_MS) { pending.current = null; return null; }
        if (number !== null && !sameNumber(p.call.number, number)) return null;
        pending.current = null;
        return p.call;
    }, []);
    // Call back: claim the caller ("Sam is calling") so two people don't ring the same patient,
    // open the dialer only, and wait for the call to end. The claim is advisory: no answer, or an
    // older backend without claims, still calls. Someone else calling: ask before taking over.
    const [takeOver, setTakeOver] = useState(null);
    const claiming = useRef(false);
    // The queue row whose claim is on its way, so its Call back button can show busy.
    const [claimingId, setClaimingId] = useState(null);
    const dial = useCallback((call) => {
        const row = call.row;
        const to = callBackNumber(row);
        const leadId = call.lead?.id ?? null;
        // The dialer widget (Twilio or desk) takes the request itself, on the channel they called in on.
        if (to && requestDialer({ to, leadId, callId: row.id, provider: row.provider })) { awaitCallEnd(call); return; }
        const dialer = callBackDialer({ number: to, deskCanCall: Boolean(outbound?.canCall), twilioCanUse: false });
        if (dialer === CALL_BACK_DIALER.NONE) { enqueueSnackbar("This caller's number was withheld, so the call can't be returned.", { variant: 'info' }); return; }
        if (dialer === CALL_BACK_DIALER.HANDSET) { enqueueSnackbar(`Dial ${formatUkPhone(to)} on your phone, then use No answer or Create lead on the row.`, { variant: 'info' }); return; }
        outbound.startCall({ to, leadId });
        // No desk chosen on this computer: the picker opens first and may be cancelled, so don't
        // wait on a call that may never start. The row's own actions stay.
        if (!outbound.desk) return;
        awaitCallEnd(call);
    }, [awaitCallEnd, enqueueSnackbar, outbound]);
    const claimThenDial = useCallback(async (call, qrow, force = false) => {
        if (claiming.current) return;
        claiming.current = true;
        setClaimingId(qrow.id);
        const forPractice = practiceRef.current;
        let result;
        try {
            result = await claimNumber(forPractice, qrow.number, { takeOver: force });
        } finally {
            claiming.current = false;
            setClaimingId(null);
        }
        // The practice changed while the claim was on its way: don't ring the old practice's caller.
        if (String(practiceRef.current) !== String(forPractice)) return;
        if (result.conflict) { setTakeOver({ call, qrow, claim: readClaim(result.claim) }); return; }
        if (!result.ok && !result.uncertain && result.status !== 404) { enqueueSnackbar(result.message, { variant: 'error' }); return; }
        dial(call);
        changed();
    }, [changed, dial, enqueueSnackbar]);
    const startCallBack = useCallback((input) => {
        const call = usableCall(input, 'startCallBack');
        if (!call) return;
        // A queue row has a number to claim; an All-calls row just dials.
        if (input.callRow && input.number) claimThenDial(call, input);
        else dial(call);
    }, [claimThenDial, dial]);
    // Call back from anywhere, with a queue row, a Call Logs row or a missed-call pill: the same thing as
    // the row's button. Claim the caller when there is a number to claim, open ONLY the dialer (no outcome
    // window, no navigation), and the widget asks "Did they answer?" when the call ends.
    const openCallBack = useCallback((input) => {
        const call = usableCall(input, 'openCallBack');
        if (!call) return;
        if (input.callRow && input.number) claimThenDial(call, input);
        else if (call.source === 'pill' && call.number) claimThenDial(call, { id: call.id, number: call.number, numberDisplay: formatUkPhone(call.number) });
        else dial(call);
    }, [claimThenDial, dial]);

    // The pill saw the user's own outbound call end answered with nothing pressed.
    const noteOwnCallNeedsOutcome = useCallback((pillCall) => {
        const own = normaliseCall(pillCall);
        if (!own) return;
        // The backend says it was answered with nothing done yet: no question, straight to the screens.
        openOutcome(takePending(own.number || '-') || own, { afterCall: true });
    }, [openOutcome, takePending]);
    // The backend logged it (rang out / busy): nothing left to ask for a call-back that was waiting.
    const noteOwnCallAutoLogged = useCallback((pillCall) => {
        const own = normaliseCall(pillCall);
        if (own) takePending(own.number || '-');
        changed();
    }, [changed, takePending]);

    useEffect(() => {
        const onOpen = (e) => {
            const { action, call, options } = e.detail || {};
            if (action === CALL_FLOW_ACTION.OUTCOME) openOutcome(call, options);
            else if (action === CALL_FLOW_ACTION.CREATE_LEAD) openCreateLeadPublic(call);
            else if (action === CALL_FLOW_ACTION.LINK_LEAD) openLinkLead(call);
            else if (action === CALL_FLOW_ACTION.CALL_BACK) openCallBack(call);
        };
        const onAwait = (e) => awaitCallEnd(e.detail?.call);
        const onTwilioEnded = (e) => {
            const forPractice = practiceRef.current;
            if (e.detail?.practiceId != null && String(e.detail.practiceId) !== String(forPractice)) return;
            // The call that ended must be the one dialled for the waiting row (when the widget says which).
            const waiting = takePending(e.detail?.to || null);
            // A moment for the ended call to land, then the widget asks how it went: the row the call-back
            // was for, else the number's owed missed call, else just the number that was dialled.
            const { to = '', leadId = null, leadName = '' } = e.detail || {};
            timers.current.push(setTimeout(async () => {
                let call = waiting;
                if (!call && to) {
                    const owed = await findOwedMissedCall(forPractice, to).catch(() => ({ row: null }));
                    call = owed?.row ? normaliseCall(owed.row) : null;
                }
                if (!call && (to || leadId != null)) call = dialedCall({ number: to, leadId, leadName });
                if (call && String(practiceRef.current) === String(forPractice)) askInWidget(call);
            }, 800));
        };
        const timers = { current: [] };
        window.addEventListener(CALL_FLOW_EVENT, onOpen);
        window.addEventListener(CALL_FLOW_AWAIT_END_EVENT, onAwait);
        window.addEventListener(TWILIO_CALL_ENDED_EVENT, onTwilioEnded);
        return () => {
            window.removeEventListener(CALL_FLOW_EVENT, onOpen);
            window.removeEventListener(CALL_FLOW_AWAIT_END_EVENT, onAwait);
            window.removeEventListener(TWILIO_CALL_ENDED_EVENT, onTwilioEnded);
            timers.current.forEach(clearTimeout);
        };
    }, [askInWidget, awaitCallEnd, openCallBack, openCreateLeadPublic, openLinkLead, openOutcome, takePending]);

    const api = useMemo(() => ({
        openOutcome, openCreateLead: openCreateLeadPublic, openLinkLead, startCallBack, openCallBack, awaitCallEnd, noteOwnCallNeedsOutcome, noteOwnCallAutoLogged, answerAsk, askAboutCall, forgetAsk, logNoAnswer, claimingId,
    }), [answerAsk, askAboutCall, forgetAsk, logNoAnswer, awaitCallEnd, claimingId, noteOwnCallAutoLogged, noteOwnCallNeedsOutcome, openCallBack, openCreateLeadPublic, openLinkLead, openOutcome, startCallBack]);

    // ── Rendering ────────────────────────────────────────────────────────────
    let screen = null;
    const current = flow && String(flow.practiceId) === String(practiceId) && isAuthenticated ? flow : null;
    if (current) {
        const { call, kind, opts } = current;
        if (kind === KIND.OUTCOME && call.lead?.id) {
            screen = (
                // The outcome screen for the patient's stage: the contact popup for an enquiry, a short
                // choice for everyone else (CallOutcomeScreen). A chunk that won't load closes the flow.
                <FlowBoundary key={`outcome-${current.seq}`} onFail={close}>
                    <CallOutcomeScreen
                        practiceId={practiceId}
                        call={call}
                        initialCard={opts.initialCard}
                        hideNoAnswer={Boolean(opts.afterCall)}
                        onChoose={(key) => chooseStageOutcome(current, key)}
                        onLeadStepComplete={(result) => onLeadStepComplete(current, result)}
                        onClose={() => closeScreen(current)}
                        onOpenPatient={() => { navigate(patientPath(practiceId, call.lead.id)); closeScreen(current); }}
                        onNeedsLeadPage={() => {
                            // Archived and closed patients get the short choice, so the popup only asks for
                            // the lead page if the patient changed stage while it loaded. Nothing is recorded
                            // for them here: "Spoke" was saved before, although nobody had spoken.
                            warn('This patient has moved on: log the call on their page.');
                            navigate(patientPath(practiceId, call.lead.id));
                            closeScreen(current);
                        }}
                    />
                </FlowBoundary>
            );
        } else if (kind === KIND.LINK) {
            screen = (
                <LinkLeadDialog
                    key={`link-${current.seq}`}
                    practiceId={practiceId}
                    call={call}
                    onLinked={(lead) => afterLinked(current, lead)}
                    onCreateInstead={() => { startWizard(call, opts); }}
                    onClose={(uncertain) => { close(); if (uncertain) changed(); }}
                />
            );
        }
        // Whatever fails to render, the flow closes; it never takes the page down.
        screen = <FlowBoundary key={`boundary-${current.seq}-${kind}`} onFail={close}>{screen}</FlowBoundary>;
    }

    return (
        <CallFlowContext.Provider value={api}>
            {children}
            {screen}
            {takeOver && String(practiceId) === String(practiceRef.current) && (
                <Dialog open onClose={() => setTakeOver(null)} maxWidth="xs" fullWidth aria-labelledby="take-over-title" sx={{ zIndex: callLayer('DIALOG') }}>
                    <DialogTitle id="take-over-title">{claimChip(takeOver.claim).text}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            {takeOver.claim?.byName || 'Someone'} is already calling {takeOver.call.lead?.name || takeOver.qrow.numberDisplay}. Take over and call them yourself?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTakeOver(null)}>Leave it</Button>
                        <Button variant="contained" onClick={() => { const t = takeOver; setTakeOver(null); claimThenDial(t.call, t.qrow, true); }}>Take over and call</Button>
                    </DialogActions>
                </Dialog>
            )}
        </CallFlowContext.Provider>
    );
}

CallFlowProvider.propTypes = { children: PropTypes.node };
CallFlowProvider.defaultProps = { children: null };
